import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Path, Stop } from 'react-native-svg';

import {
	AvatarPlaceholderPng,
	IconRemoveFriendSvg,
	IconSendGiftSvg,
} from '../../../assets/friends';
import { goldGlow } from '../../../shared/glowStyles';
import { getAvatarUrl } from '../../../utils/axiosInstance';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import { Friend, FriendStatus, friendStatusLabel } from './friendsCatalog';
import {
	FRIENDS_FONT,
	friendsColors,
	hairline,
	IN_GAME_DOT_FILL,
	inset,
	RADIUS_LG,
	RADIUS_MD,
} from './friendsTheme';

/**
 * Figma "Friend / ShadowKing" (4244:41104) and its nine siblings - one row of the friends list.
 * Every row is the same 334x76 card: a 60 avatar with its level badge, the username over a status
 * line, and the two 40 action buttons.
 *
 * The avatar is the player's own picture, or the file's dashed placeholder disc when they have
 * none. An incoming friend request uses the same row with accept / reject in place of the two
 * friend actions; the design has no request row, so it borrows this one.
 */
const ROW_DESIGN_WIDTH = 334;
const ROW_DESIGN_HEIGHT = 76;

const PADDING_X = 10;
const PADDING_Y = 8;
const ROW_GAP = 10;

const AVATAR_BOX = 60;
const ARTWORK_SIZE = 56;
const ARTWORK_X = 2;
const BADGE_SIZE = 22;
const BADGE_Y = 38;
const BADGE_STROKE = 1.5;

const NAME_SIZE = 18;
const STATUS_SIZE = 12;
const DOT_SIZE = 10;
const IDENTITY_GAP = 4;
const STATUS_GAP = 6;

const ACTIONS_GAP = 8;
const BUTTON_SIZE = 40;
const GIFT_ICON = { width: 18, height: 20 };
/** The remove glyph is 20x24 and the design pins it rather than centring it - 1 right of centre. */
const REMOVE_ICON = { width: 20, height: 24, x: 11, y: 8 };
const ANSWER_ICON = 18;

const statusColor = (status: FriendStatus) =>
	status.kind === 'online'
		? friendsColors.online
		: status.kind === 'in-game'
			? friendsColors.inGame
			: friendsColors.offline;

/**
 * Online and offline dots are flat fills, so they are cheaper as a View. "In Game" is the one the
 * design paints as a gradient, which needs an `<Svg>` - and its gradient id has to be unique per
 * instance for the same reason `useSvgId` exists.
 */
const StatusDot = ({ status, size }: { status: FriendStatus; size: number }) => {
	const fillId = useSvgId('friendStatusDot');

	if (status.kind !== 'in-game') {
		return (
			<View
				style={{
					width: size,
					height: size,
					borderRadius: size / 2,
					backgroundColor: statusColor(status),
				}}
			/>
		);
	}

	return (
		<View style={{ width: size, height: size }}>
			<Svg width={size} height={size} viewBox='0 0 10 10'>
				<Defs>
					<LinearGradient id={fillId} x1='2.125' y1='0' x2='3.279' y2='9.872'>
						{IN_GAME_DOT_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Circle cx='5' cy='5' r='5' fill={`url(#${fillId})`} />
			</Svg>
		</View>
	);
};

/** The accept tick and reject cross, drawn in the gift / remove buttons' gold. */
const AnswerGlyph = ({ accept, size }: { accept: boolean; size: number }) => (
	<Svg width={size} height={size} viewBox='0 0 18 18'>
		<Path
			d={accept ? 'M3 9.5 L7.2 13.5 L15 4.5' : 'M4 4 L14 14 M14 4 L4 14'}
			stroke={friendsColors.gold300}
			strokeWidth={2.6}
			strokeLinecap='round'
			strokeLinejoin='round'
			fill='none'
		/>
	</Svg>
);

type Props = {
	friend: Friend;
	scale: number;
	/** No gift service yet; the button is left out while this is absent. */
	onSendGift?: (friend: Friend) => void;
	onRemove?: (friend: Friend) => void;
	/** Turns the row into an incoming request: accept / reject replace gift / remove. */
	onAccept?: (friend: Friend) => void;
	onReject?: (friend: Friend) => void;
	statusText?: string;
	/** A request for this player is in flight; the buttons ignore presses until it settles. */
	busy?: boolean;
};

export const FriendRow = ({
	friend,
	scale,
	onSendGift,
	onRemove,
	onAccept,
	onReject,
	statusText,
	busy = false,
}: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const isRequest = !!(onAccept || onReject);
	const press = (handler?: (friend: Friend) => void) =>
		handler && !busy ? () => handler(friend) : undefined;

	return (
		<View style={styles.row}>
			<View style={styles.avatar}>
				<Image
					source={
						friend.avatarFileId ? { uri: getAvatarUrl(friend.avatarFileId) } : AvatarPlaceholderPng
					}
					style={[styles.artwork, friend.avatarFileId ? styles.photo : null]}
					resizeMode={friend.avatarFileId ? 'cover' : 'contain'}
				/>
				{/* "Level badge" (4244:41107) is a gold/300 disc with a 1.5 text/on-gold stroke - a
				    bordered View rather than an export, so the number sits on it at any scale. */}
				<View style={styles.badge}>
					<Text numberOfLines={1} style={styles.badgeLabel}>
						{friend.level}
					</Text>
				</View>
			</View>

			<View style={styles.identity}>
				<Text numberOfLines={1} style={styles.name}>
					{friend.username}
				</Text>
				<View style={styles.status}>
					{statusText ? null : <StatusDot status={friend.status} size={DOT_SIZE * scale} />}
					<Text
						numberOfLines={1}
						style={[
							styles.statusLabel,
							{ color: statusText ? friendsColors.gold300 : statusColor(friend.status) },
						]}
					>
						{statusText ?? friendStatusLabel(friend.status)}
					</Text>
				</View>
			</View>

			<View style={[styles.actions, busy && styles.busy]}>
				{isRequest ? (
					<>
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_A11Y_ACCEPT_FRIEND_REQUEST_FROM', {
								defaultValue: "Accept {{username}}'s friend request",
								username: friend.username,
							})}
							onPress={press(onAccept)}
							style={styles.actionButton}
						>
							<View>
								<AnswerGlyph accept size={ANSWER_ICON * scale} />
							</View>
						</Pressable>
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_A11Y_REJECT_FRIEND_REQUEST_FROM', {
								defaultValue: "Reject {{username}}'s friend request",
								username: friend.username,
							})}
							onPress={press(onReject)}
							style={styles.actionButton}
						>
							<View>
								<AnswerGlyph accept={false} size={ANSWER_ICON * scale} />
							</View>
						</Pressable>
					</>
				) : (
					<>
						{onSendGift ? (
							<Pressable
								accessibilityRole='button'
								accessibilityLabel={t('T_SITE_A11Y_SEND_GIFT_TO', {
									defaultValue: 'Send a gift to {{username}}',
									username: friend.username,
								})}
								onPress={press(onSendGift)}
								style={styles.actionButton}
							>
								{/* Wrapped for the same painting-order reason as the modal's tab icons: a bare
								    SVGR `<svg>` carries no position on web and slips under its positioned
								    siblings. */}
								<View>
									<IconSendGiftSvg
										width={GIFT_ICON.width * scale}
										height={GIFT_ICON.height * scale}
									/>
								</View>
							</Pressable>
						) : null}

						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_A11Y_REMOVE_FRIEND', {
								defaultValue: 'Remove {{username}} from your friends',
								username: friend.username,
							})}
							onPress={press(onRemove)}
							style={styles.actionButton}
						>
							<View style={styles.removeIcon}>
								<IconRemoveFriendSvg
									width={REMOVE_ICON.width * scale}
									height={REMOVE_ICON.height * scale}
								/>
							</View>
						</Pressable>
					</>
				)}
			</View>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			width: ROW_DESIGN_WIDTH * scale,
			height: ROW_DESIGN_HEIGHT * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: ROW_GAP * scale,
			paddingHorizontal: inset(PADDING_X, scale),
			paddingVertical: inset(PADDING_Y, scale),
			borderRadius: RADIUS_LG * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.border,
			backgroundColor: friendsColors.surface,
		},
		avatar: {
			width: AVATAR_BOX * scale,
			height: AVATAR_BOX * scale,
		},
		artwork: {
			position: 'absolute',
			left: ARTWORK_X * scale,
			top: 0,
			width: ARTWORK_SIZE * scale,
			height: ARTWORK_SIZE * scale,
		},
		badge: {
			position: 'absolute',
			left: 0,
			top: BADGE_Y * scale,
			width: BADGE_SIZE * scale,
			height: BADGE_SIZE * scale,
			borderRadius: (BADGE_SIZE / 2) * scale,
			borderWidth: BADGE_STROKE * scale,
			borderColor: friendsColors.onGold,
			backgroundColor: friendsColors.gold300,
			alignItems: 'center',
			justifyContent: 'center',
		},
		// Figma "DS/Label/S": Sofia Sans SemiBold 10, line-height 1.2.
		badgeLabel: {
			color: friendsColors.onGold,
			fontFamily: FRIENDS_FONT,
			fontSize: 10 * scale,
			fontWeight: '600',
			lineHeight: 10 * 1.2 * scale,
			textAlign: 'center',
		},
		identity: {
			flex: 1,
			gap: IDENTITY_GAP * scale,
		},
		// Figma "DS/Heading/M": Sofia Sans Bold 18, line-height 1.3.
		name: {
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: NAME_SIZE * scale,
			fontWeight: '700',
			lineHeight: NAME_SIZE * 1.3 * scale,
		},
		status: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: STATUS_GAP * scale,
		},
		// Figma "DS/Body/S": Sofia Sans Medium 12, line-height 1.4.
		statusLabel: {
			flex: 1,
			fontFamily: FRIENDS_FONT,
			fontSize: STATUS_SIZE * scale,
			fontWeight: '500',
			lineHeight: STATUS_SIZE * 1.4 * scale,
		},
		actions: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: ACTIONS_GAP * scale,
		},
		busy: {
			opacity: 0.5,
		},
		photo: {
			borderRadius: (ARTWORK_SIZE / 2) * scale,
		},
		actionButton: {
			width: BUTTON_SIZE * scale,
			height: BUTTON_SIZE * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
			...goldGlow,
		},
		removeIcon: {
			position: 'absolute',
			left: REMOVE_ICON.x * scale,
			top: REMOVE_ICON.y * scale,
		},
	});

export default FriendRow;
